import { Link } from 'react-router-dom';
import { Phone, Mail, MapPin, Facebook, Linkedin, MessageCircle, ArrowRight } from 'lucide-react';
import { motion } from 'framer-motion';

const Footer = () => {
  const currentYear = new Date().getFullYear();

  return (
    <footer className="bg-[#0a0a0a] text-white relative overflow-hidden" data-testid="footer">
      {/* Background Effects */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-0 left-0 w-96 h-96 bg-[#3498DB]/5 rounded-full filter blur-[150px]" />
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-[#27AE60]/5 rounded-full filter blur-[150px]" />
      </div>
      
      {/* Top CTA Banner */}
      <div className="border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 py-12">
          <div className="flex flex-col md:flex-row justify-between items-center gap-6">
            <div>
              <h3 className="text-2xl md:text-3xl font-bold mb-2">Ready to start your journey?</h3>
              <p className="text-gray-400">Contact us today for a free consultation</p>
            </div>
            <Link 
              to="/contact"
              className="group inline-flex items-center gap-3 px-8 py-4 bg-gradient-to-r from-[#3498DB] to-[#27AE60] text-white font-semibold rounded-full transition-all duration-300 hover:shadow-[0_0_30px_rgba(52,152,219,0.5)]"
            >
              Get Started
              <ArrowRight className="transition-transform duration-300 group-hover:translate-x-2" size={20} />
            </Link>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-16 relative">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Company Info */}
          <div>
            <motion.div 
              className="bg-white rounded-xl px-3 py-2 inline-block mb-6"
              whileHover={{ scale: 1.05 }}
            >
              <img 
                src="https://customer-assets.emergentagent.com/job_samrelocation/artifacts/ezz0kk2s_image.png" 
                alt="SAM Relocation Group" 
                className="h-8 w-auto"
              />
            </motion.div>
            <p className="text-gray-400 leading-relaxed mb-6">
              Your trusted partner for business setup, relocation, and corporate services in the UAE.
            </p>
            <div className="flex gap-4">
              <motion.a
                href="https://www.facebook.com/profile.php?id=61585486726158"
                target="_blank"
                rel="noopener noreferrer"
                className="w-12 h-12 rounded-xl bg-white/5 hover:bg-[#1877F2] border border-white/10 flex items-center justify-center transition-all duration-300 cursor-pointer"
                whileHover={{ scale: 1.1, y: -5 }}
                data-testid="footer-facebook-link"
                aria-label="Facebook"
              >
                <Facebook size={20} />
              </motion.a>
              <motion.a
                href="https://www.linkedin.com/company/relocation-dubai"
                target="_blank"
                rel="noopener noreferrer"
                className="w-12 h-12 rounded-xl bg-white/5 hover:bg-[#0077B5] border border-white/10 flex items-center justify-center transition-all duration-300 cursor-pointer"
                whileHover={{ scale: 1.1, y: -5 }}
                data-testid="footer-linkedin-link"
                aria-label="LinkedIn"
              >
                <Linkedin size={20} />
              </motion.a>
              <motion.a
                href="https://wa.me/971563528126?text=Hello%20SAM%20Relocation%20Group"
                target="_blank"
                rel="noopener noreferrer"
                className="w-12 h-12 rounded-xl bg-white/5 hover:bg-[#25D366] border border-white/10 flex items-center justify-center transition-all duration-300 cursor-pointer"
                whileHover={{ scale: 1.1, y: -5 }}
                data-testid="footer-whatsapp-link"
                aria-label="WhatsApp"
              >
                <MessageCircle size={20} />
              </motion.a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="text-lg font-bold mb-6 text-white">Quick Links</h4>
            <ul className="space-y-4">
              {[
                { name: 'Home', path: '/' },
                { name: 'About Us', path: '/about' },
                { name: 'Services', path: '/services' },
                { name: 'Our Team', path: '/team' },
                { name: 'Why Choose Us', path: '/why-choose-us' },
              ].map((link) => (
                <li key={link.path}>
                  <Link 
                    to={link.path} 
                    className="text-gray-400 hover:text-white transition-colors inline-flex items-center gap-2 group"
                    data-testid={`footer-link-${link.name.toLowerCase().replace(/\s+/g, '-')}`}
                  >
                    <span className="w-0 h-[1px] bg-[#3498DB] group-hover:w-4 transition-all duration-300" />
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Services */}
          <div>
            <h4 className="text-lg font-bold mb-6 text-white">Our Services</h4>
            <ul className="space-y-4 text-gray-400">
              {[
                'Relocation & Expatriation',
                'Business Setup',
                'Corporate Services',
                'Financial Consulting',
                'Tax & Compliance',
              ].map((service) => (
                <li key={service} className="flex items-center gap-2 group cursor-default">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#27AE60] group-hover:scale-150 transition-transform duration-300" />
                  {service}
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h4 className="text-lg font-bold mb-6 text-white">Contact Us</h4>
            <ul className="space-y-5">
              <li className="flex items-start gap-4 text-gray-400 group">
                <div className="w-10 h-10 rounded-xl bg-[#3498DB]/10 flex items-center justify-center flex-shrink-0 group-hover:bg-[#3498DB]/20 transition-colors">
                  <MapPin size={18} className="text-[#3498DB]" />
                </div>
                <span className="text-sm leading-relaxed">
                  Office no 08-149, We Work, One Central<br />
                  Dubai World Trade Centre<br />
                  Dubai, UAE
                </span>
              </li>
              <li className="flex items-center gap-4 text-gray-400 group">
                <div className="w-10 h-10 rounded-xl bg-[#27AE60]/10 flex items-center justify-center flex-shrink-0 group-hover:bg-[#27AE60]/20 transition-colors">
                  <Phone size={18} className="text-[#27AE60]" />
                </div>
                <a href="tel:+971563528126" className="hover:text-white transition-colors">
                  +971 563 528 126
                </a>
              </li>
              <li className="flex items-center gap-4 text-gray-400 group">
                <div className="w-10 h-10 rounded-xl bg-[#5DADE2]/10 flex items-center justify-center flex-shrink-0 group-hover:bg-[#5DADE2]/20 transition-colors">
                  <Mail size={18} className="text-[#5DADE2]" />
                </div>
                <a href="mailto:groupsamrelocate@gmail.com" className="hover:text-white transition-colors text-sm">
                  groupsamrelocate@gmail.com
                </a>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom Section */}
        <div className="mt-16 pt-8 border-t border-white/10">
          <div className="flex flex-col md:flex-row justify-between items-center gap-6">
            <div className="flex flex-col sm:flex-row items-center gap-4 text-sm text-gray-400">
              <div className="flex items-center gap-2">
                <span className="w-2 h-2 rounded-full bg-[#27AE60] animate-pulse" />
                <span className="font-medium text-white">Business Hours:</span>
              </div>
              <span>Monday - Friday: 8:00 AM - 7:00 PM</span>
              <span className="hidden sm:inline text-white/20">|</span>
              <span>Saturday & Sunday: Closed</span>
            </div>
            <div className="text-gray-500 text-sm">
              &copy; {currentYear} SAM Relocation Group. All rights reserved.
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;